<template>
  <div style="background-color:white;" class="searchbar mt-2">
    <div class="searchbar__control" :class="{'searchbar__control--members': page !== 'blog'}">
      <i class="fa fas fa-search searchbar__icon" aria-hidden="true"></i>
      <input class="searchbar__input" :placeholder="description" v-model="search"
             @input="onChange"
             @keydown.down="onArrowDown"
             @keydown.up="onArrowUp"
             @keydown.enter.prevent="onEnter"
             type="search"/>
      <button v-if="showReset" type="button" class="autocomplete-reset" @click.stop="resetSearch"
              aria-label="Return to previous search" title="Return to previous search">
        <i class="fa fa-undo" aria-hidden="true"></i>
        <span>Back</span>
      </button>
    </div>
    <ul v-if="page!='blog'" v-show="isOpen" :class="results.length < 1 ? 'autocomplete-results-empty' : 'autocomplete-results'">
      <li v-for="(result, i) in results"
          :key="i"
          @click="setResult(result)"
          class="autocomplete-result"
          :class="{ 'is-active': i === arrowCounter }"
      >
        {{ autocompleteResultLabel(result) }}
      </li>
      <li v-if="results.length < 1">
        <label>No Results Found</label>
      </li>
    </ul>
    <ul v-if="page=='blog'" v-show="isOpen" :class="results.length < 1 ? 'autocomplete-results-empty' : 'autocomplete-results'">
      <li v-for="(result, i) in results"
          :key="i"
          @click="setResult(result)"
          class="autocomplete-result"
          :class="{ 'is-active': i === arrowCounter }"
      >
        {{ blogResultLabel(result) }}
      </li>
      <li v-if="results.length < 1">
        <label>No Results Found</label>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';
import axios from "@/axios";

function lowerCaseDisplay(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

export default defineComponent({
  name: 'auto-complete',
  props: {
    name: String,
    page: String,
    list: {
      default: ['']
    },
    initialValue: {
      type: String,
      default: ''
    },
    showReset: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      search: this.page === 'blog' ? (this.initialValue || '') : lowerCaseDisplay(this.initialValue),
      isOpen: false,
      results: [] as Array<any>,
      autoCompleteList: Array.isArray(this.list) ? this.list : [] as Array<any>,
      arrowCounter: -1,
      description: "Smart Search Businesses, Attractions, Landmarks, Towns"
    }
  },
  created() {
    if (this.page == 'blog') {
      this.description = 'Search Town or Postcode'
    }
    // Set initial search value if provided
    if (this.initialValue) {
      this.search = this.page === 'blog' ? this.initialValue : lowerCaseDisplay(this.initialValue);
    }
  },
  watch: {
    initialValue(newVal) {
      this.search = this.page === 'blog' ? (newVal || '') : lowerCaseDisplay(newVal);
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  unmounted() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    resetSearch() {
      this.search = '';
      this.results = [];
      this.isOpen = false;
      this.arrowCounter = -1;
      this.$emit('reset');
    },
    blogResultLabel(result) {
      if (typeof result === 'string') {
        const parts = result.split('-');
        return (parts[1] || parts[0] || '').trim();
      }
      if (result && typeof result === 'object') {
        return result.name || result.text || '';
      }
      return '';
    },
    autocompleteResultLabel(result) {
      const resultName = typeof result === 'object'
        ? (result?.name || result?.text || '')
        : result;
      return lowerCaseDisplay(resultName);
    },
    async fetchAutocomplete(key) {
      try {
        const response = await axios.get("/" + this.page + "/autocomplete/" + key);
        this.autoCompleteList = this.normaliseResults(response.data);
        this.filterResults();
      } catch (error) {
        this.autoCompleteList = [];
        this.results = [];
      }
    },
    async fetchBlogAutoComplete(key) {
      try {
        const response = await axios.get("/blog/town/autocomplete/" + key);
        this.autoCompleteList = this.normaliseResults(response.data);
        this.filterResults();
      } catch (error) {
        this.autoCompleteList = [];
        this.results = [];
      }
    },
    async setResult(result) {
      const resultName = typeof result === 'object' ? (result?.name || result?.text || '') : (result || '');
      this.search = this.page === 'blog' ? resultName : lowerCaseDisplay(resultName);
      this.isOpen = false;
      
      // If it's a business search, try to get location data
      if (this.page !== 'blog' && result) {
        // Check if result already has location data (from API)
        if (typeof result === 'object' && result.location) {
          this.$emit('clicked', {
            name: resultName,
            location: result.location
          });
        } else {
          // Try to fetch location data from backend
          try {
            const response = await axios.get("/" + this.page + "/autocomplete-details/" + encodeURIComponent(resultName));
            if (response.data && response.data.location) {
              this.$emit('clicked', {
                name: resultName,
                location: response.data.location
              });
            } else {
              // Fallback to just the name if no location data
              this.$emit('clicked', resultName);
            }
          } catch (error) {
            // If API fails, just emit the name (current behavior)
            this.$emit('clicked', resultName);
          }
        }
      } else {
        this.$emit('clicked', this.search);
      }
    },
    filterResults() {
      if (!Array.isArray(this.autoCompleteList)) {
        this.results = [];
        return;
      }

      // Handle both string and object responses from API
      if (this.autoCompleteList.length > 0 && typeof this.autoCompleteList[0] === 'object') {
        // If API returns objects, filter by name property
        this.results = this.autoCompleteList.filter(item => 
          (item.name || item.text || '').toLowerCase().indexOf(this.search.toLowerCase()) > -1
        );
      } else {
        // If API returns strings, use original filter
        this.results = this.autoCompleteList.filter(item => item.toLowerCase().indexOf(this.search.toLowerCase()) > -1);
      }
    },
    normaliseResults(payload) {
      if (Array.isArray(payload)) {
        return payload;
      }
      if (payload && Array.isArray(payload.data)) {
        return payload.data;
      }
      return [];
    },
    onChange() {
      if (this.search.length <= 0) {
        this.results = [];
        this.arrowCounter = -1;
        this.isOpen = false;
        this.$emit('clicked', '');
        return;
      }
      if (this.search.length >= 3) {
        if(this.page != 'blog') {
          this.fetchAutocomplete(this.search.toLowerCase());
        } else {
          this.fetchBlogAutoComplete(this.search.toLowerCase())
        }
        this.isOpen = true;
      } else {
        this.isOpen = false;
      }
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.arrowCounter = -1;
        this.isOpen = false;
      }
    },
    onArrowDown() {
      if (this.arrowCounter < this.results.length) {
        this.arrowCounter = this.arrowCounter + 1;
      }
    },
    onArrowUp() {
      if (this.arrowCounter > 0) {
        this.arrowCounter = this.arrowCounter - 1;
      }
    },
    async onEnter() {
      const selectedResult = this.results[this.arrowCounter] ?? this.results[0];
      if (!selectedResult) {
        this.isOpen = false;
        return;
      }
      const resultName = typeof selectedResult === 'object' ? (selectedResult.name || selectedResult.text || '') : selectedResult;
      this.search = this.page === 'blog' ? resultName : lowerCaseDisplay(resultName);
      this.arrowCounter = -1;
      await this.setResult(selectedResult)
    }
  },
});
</script>

<style scoped>
.autocomplete {
  position: relative;
}

.searchbar {
  position: relative;
}

.searchbar__control {
  display: flex;
  min-height: 2.35rem;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.4rem 0.25rem 0.65rem;
  border: 1px solid #e5e8eb;
  border-radius: 6px;
  background: #fff;
}

.searchbar__icon {
  flex: 0 0 auto;
  color: #34aade;
}

.searchbar__control--members {
  min-height: 2.15rem;
  padding: 0.15rem 0.5rem 0.15rem 0.6rem;
}

.searchbar__control--members .searchbar__input {
  order: 1;
}

.searchbar__control--members .autocomplete-reset {
  order: 2;
}

.searchbar__control--members .searchbar__icon {
  order: 3;
  margin-left: auto;
}

.searchbar__input {
  min-width: 0;
  flex: 1 1 auto;
  padding: 0 0 0 0.25rem;
  border: 0;
  outline: 0;
  background: transparent;
  font-size: 0.82rem;
}

.autocomplete-reset {
  display: inline-flex;
  flex: 0 0 auto;
  width: auto !important;
  max-width: none;
  align-items: center;
  gap: 0.3rem;
  border: 1px solid #34aade;
  border-radius: 999px;
  padding: 0.2rem 0.55rem;
  background: #f3fbff;
  color: #34aade;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
}

.autocomplete-reset:hover,
.autocomplete-reset:focus-visible {
  background: #34aade;
  color: #fff;
}

.autocomplete-results {
  padding: 0;
  margin: 0;
  border: 1px solid #eeeeee;
  height: 120px;
  min-height: 1em;
  max-height: 6em;
  overflow: auto;
}

.autocomplete-results-empty {
  padding: 0;
  margin: 0;
  border: 1px solid #eeeeee;
  height: 50px;
  min-height: 0.5em;
  overflow: auto;
}

.autocomplete-result {
  list-style: none;
  text-align: left;
  padding: 4px 2px;
  font-size: 0.82rem;
  cursor: pointer;
}

.autocomplete-result.is-active,
.autocomplete-result:hover {
  background-color: #34aade;
  color: white;
}
</style>
